<template>
  <!-- 详情弹窗 References 网格：统一 80px 方形块 + @token 角标，点击盖层放大预览 -->
  <div class="ref-grid">
    <button
      v-for="it in items"
      :key="`${it.token}-${it.url}`"
      type="button"
      class="ref-cell"
      @click="preview = it"
    >
      <video
        v-if="it.format === 'video'"
        :src="it.url"
        preload="metadata"
        muted
        playsinline
        class="ref-media"
      />
      <img v-else-if="it.format === 'image'" :src="it.url" alt="" class="ref-media" />
      <span v-else class="ref-audio-face">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
        </svg>
      </span>
      <!-- hover 遮罩 + 放大图标 -->
      <span class="ref-hover">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35M11 8v6M8 11h6"/>
        </svg>
      </span>
      <!-- 底部引用名 —— 与创作页素材角标一致 -->
      <span class="ref-token">{{ it.token }}</span>
    </button>

    <!-- 素材放大预览（盖在详情弹窗之上） -->
    <Teleport to="body">
      <div v-if="preview" class="ref-preview-overlay" @click.stop="preview = null">
        <button type="button" class="ref-preview-close" aria-label="Close preview" @click="preview = null">✕</button>
        <video
          v-if="preview.format === 'video'"
          :src="preview.url"
          controls
          autoplay
          playsinline
          class="ref-preview-video"
          @click.stop
        />
        <div v-else-if="preview.format === 'audio'" class="ref-preview-audio" @click.stop>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/>
          </svg>
          <p class="ref-preview-filename">{{ preview.url.split('/').pop() }}</p>
          <audio :src="preview.url" controls autoplay />
        </div>
        <img v-else :src="preview.url" alt="" class="ref-preview-img" @click.stop />
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { mentionToken, type MentionFormat } from '~/utils/seedanceMentions';

const props = defineProps<{
  imageUrls?: string[];
  videoUrls?: string[];
  audioUrls?: string[];
}>();

interface RefItem {
  url: string;
  format: MentionFormat;
  token: string;
}

const items = computed<RefItem[]>(() => [
  ...(props.imageUrls ?? []).map((url, i) => ({
    url,
    format: 'image' as const,
    token: mentionToken('image', i + 1),
  })),
  ...(props.videoUrls ?? []).map((url, i) => ({
    url,
    format: 'video' as const,
    token: mentionToken('video', i + 1),
  })),
  ...(props.audioUrls ?? []).map((url, i) => ({
    url,
    format: 'audio' as const,
    token: mentionToken('audio', i + 1),
  })),
]);

const preview = ref<RefItem | null>(null);
</script>

<style scoped>
.ref-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}
.ref-cell {
  position: relative;
  width: 80px;
  height: 80px;
  padding: 0;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 0.5rem;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
}
.ref-media {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.ref-audio-face {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  color: rgba(255, 255, 255, 0.5);
}
.ref-hover {
  pointer-events: none;
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.4);
  color: white;
  opacity: 0;
  transition: opacity 0.15s;
}
.ref-cell:hover .ref-hover {
  opacity: 1;
}
.ref-token {
  pointer-events: none;
  position: absolute;
  inset-inline: 0;
  bottom: 4px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  color: white;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.9);
}
/* 放大预览盖层（详情弹窗 z-index 100 之上） */
.ref-preview-overlay {
  position: fixed;
  inset: 0;
  z-index: 140;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
}
.ref-preview-close {
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  color: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  cursor: pointer;
}
.ref-preview-close:hover {
  background: black;
  color: white;
}
.ref-preview-video,
.ref-preview-img {
  max-height: 85vh;
  max-width: 100%;
  border-radius: 0.75rem;
}
.ref-preview-img {
  object-fit: contain;
}
.ref-preview-audio {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  padding: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 1rem;
  background: #131313;
  color: rgba(255, 255, 255, 0.6);
}
.ref-preview-filename {
  margin: 0;
  max-width: 280px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 0.75rem;
  color: rgba(255, 255, 255, 0.6);
}
</style>
